import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { FiLock, FiUser, FiMail, FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/common/Button';

// 公开品牌信息(无需登录): 注册页贴牌展示
const fetchPublicSettings = async () => {
  const response = await fetch('/api/settings/public').then(r => r.json()).catch(() => ({}));
  return response.settings || {};
};
const fetchBackground = async () => {
  const response = await fetch('/api/settings/background').then(r => r.json()).catch(() => ({}));
  return response.url || '';
};

const Register = () => {
  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { data: publicSettings } = useQuery('publicSettings', fetchPublicSettings, {
    staleTime: 5 * 60 * 1000,
    retry: false
  });
  const { data: backgroundUrl } = useQuery('settingsBackground', fetchBackground, {
    staleTime: 5 * 60 * 1000,
    retry: false
  });
  const systemTitle = publicSettings?.brandTitle || publicSettings?.systemTitle || '运维总控';
  
  // 状态
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // 按菜单权限决定注册后落地页: 新用户默认无板块权限, 进个人资料
  const landingFor = (user) => {
    if (!user) return '/profile';
    const isAdmin = user.role === 'admin';
    const perms = Array.isArray(user.menuPermissions) ? user.menuPermissions : [];
    return (isAdmin || perms.includes('dashboard')) ? '/dashboard' : '/profile';
  };

  // 如果已登录，重定向到首页
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/profile', { replace: true });
    }
  }, [isAuthenticated, navigate]);
  
  // 处理注册
  const handleRegister = async (e) => {
    e.preventDefault();
    
    // 表单验证
    if (!username.trim() || !password.trim() || !confirmPassword.trim()) {
      setError('用户名和密码不能为空');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }
    
    if (password.length < 6) {
      setError('密码长度不能少于6个字符');
      return;
    }
    
    if (!email.trim()) {
      setError('邮箱不能为空');
      return;
    }
    
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('邮箱格式不正确');
      return;
    }
    
    setError('');
    setIsLoading(true);
    
    try {
      const user = await register(username, password, email);
      
      if (user) {
        navigate(landingFor(user), { replace: true });
      }
    } catch (error) {
      setError('注册失败，请检查输入信息');
    } finally {
      setIsLoading(false);
    }
  };
  
  // 页面动画
  const pageVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  
  return (
    <motion.div
      variants={pageVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen flex items-center justify-center bg-[#f6f8fa] px-4 py-12"
      style={backgroundUrl ? {
        backgroundImage: `url(${backgroundUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      } : undefined}
    >
      <div className="relative z-10">
        {/* 背景装饰 */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-[#ddf4ff] rounded-full opacity-40 -z-10 blur-xl transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-40 h-40 bg-[#fbefff] rounded-full opacity-40 -z-10 blur-xl transform translate-x-1/2 translate-y-1/2"></div>
      
        <motion.div
          variants={itemVariants}
          className="w-full max-w-md bg-white rounded-md shadow-sm overflow-hidden"
        >
          {/* 标题 */}
          <div className="p-8 border-b border-[#d0d7de] text-center bg-white relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-1 brand-gradient" />
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-2xl sm:text-3xl font-bold gradient-text"
            >
              {systemTitle}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-2 text-[#656d76]"
            >
              注册管理员账户
            </motion.p>
          </div>
          
          {/* 注册表单 */}
          <div className="p-8">
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="bg-[#ffebe9] border border-[#ffcecb] rounded-md p-3 mb-6 flex items-center gap-3 text-[#cf222e]"
              >
                <FiAlertCircle size={18} />
                <span>{error}</span>
              </motion.div>
            )}
            
            <form onSubmit={handleRegister}>
              <motion.div variants={itemVariants} className="mb-5">
                <label className="block text-[#57606a] text-sm font-medium mb-2">
                  用户名
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#656d76]">
                    <FiUser />
                  </span>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="输入用户名"
                    className="w-full py-3 px-10 bg-[#f6f8fa] rounded-md text-[#1f2328] border border-[#d0d7de] focus:outline-none input-primary"
                  />
                </div>
              </motion.div>
              
              <motion.div variants={itemVariants} className="mb-5">
                <label className="block text-[#57606a] text-sm font-medium mb-2">
                  邮箱
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#656d76]">
                    <FiMail />
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="输入邮箱"
                    className="w-full py-3 px-10 bg-[#f6f8fa] rounded-md text-[#1f2328] border border-[#d0d7de] focus:outline-none input-primary"
                  />
                </div>
              </motion.div>
              
              <motion.div variants={itemVariants} className="mb-5">
                <label className="block text-[#57606a] text-sm font-medium mb-2">
                  密码
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#656d76]">
                    <FiLock />
                  </span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="输入密码 (至少6位)"
                    className="w-full py-3 px-10 bg-[#f6f8fa] rounded-md text-[#1f2328] border border-[#d0d7de] focus:outline-none input-primary"
                  />
                </div>
              </motion.div>
              
              <motion.div variants={itemVariants} className="mb-6">
                <label className="block text-[#57606a] text-sm font-medium mb-2">
                  确认密码
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#656d76]">
                    <FiLock />
                  </span>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="再次输入密码"
                    className="w-full py-3 px-10 bg-[#f6f8fa] rounded-md text-[#1f2328] border border-[#d0d7de] focus:outline-none input-primary"
                  />
                </div>
              </motion.div>
              
              <motion.div variants={itemVariants}>
                <Button
                  type="submit"
                  variant="primary"
                  fullWidth={true}
                  disabled={isLoading}
                >
                  {isLoading ? '注册中...' : '注册'}
                </Button>
              </motion.div>
            </form>
            
            <motion.div
              variants={itemVariants}
              className="mt-6 text-center text-[#656d76] text-sm"
            >
              已有账户？ <Link to="/login" className="brand-text hover:underline">立即登录</Link>
            </motion.div>
          </div>
        </motion.div>
        
        <motion.div
          variants={itemVariants}
          className="mt-8 text-center text-[#8b949e] text-sm"
        >
          © {new Date().getFullYear()} 授权管理系统 · 技术支持
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Register;